import { Disc3 } from 'lucide-react';
import { useAlbumCoverImage } from '@/hooks/useAlbumCoverImage';
import { hashString } from '@/utils/hashString';

interface AlbumCoverProps {
  albumId: number;
  alt: string;
  className?: string;
  coverArtUrl?: string | null; // when null/empty, render the placeholder
}

/**
 * Album artwork thumbnail. Falls back to a disc placeholder when the album has
 * no artwork or the fetch fails, so the scope selector always renders cleanly.
 */
export default function AlbumCover({ albumId, alt, className = '', coverArtUrl }: AlbumCoverProps) {
  const hasArt = coverArtUrl === undefined || !!coverArtUrl;
  // Bust the persistent cache when the underlying cover changes (its S3 key,
  // and thus this URL, carries a unique per-upload token).
  const cacheToken = coverArtUrl ? hashString(coverArtUrl) : undefined;
  const { imageUrl, isLoading, hasError } = useAlbumCoverImage(albumId, hasArt, cacheToken);

  if (!hasArt || hasError || (!isLoading && !imageUrl)) {
    return (
      <div
        className={`flex items-center justify-center bg-surface-hover text-text-muted ${className}`}
        aria-label={alt}
      >
        <Disc3 size={16} />
      </div>
    );
  }

  if (isLoading || !imageUrl) {
    return <div className={`bg-surface-hover ${className}`} aria-label={alt} />;
  }

  return <img src={imageUrl} alt={alt} className={className} />;
}
